(() => {
  if (window.__wbuPolicyFormsInitialized) return;
  window.__wbuPolicyFormsInitialized = true;
  window.__wbuPolicyFormsVersion = '2.2';

  const modalSelector = '[data-wbu-form-modal]';

  /*
   * Event delegation is installed globally.
   * Do not exit if the policy form markup is not yet in the DOM:
   * depending on theme/script loading order, page content may be parsed later.
   */
  let activeModal = null;
  let lastTrigger = null;

  /*
   * Envy creates stacking contexts around parts of the page/header.
   * A fixed modal can therefore remain visually below the header even
   * with a very high z-index. While a modal is open, move it directly
   * under <body>; restore it to its original position when closed.
   */
  const portalOwners = new WeakMap();

  function getLocale() {
    const lang = (document.documentElement.lang || 'en')
      .trim()
      .toLowerCase()
      .slice(0, 2);

    return ['en', 'fr', 'de'].includes(lang) ? lang : 'en';
  }

  function clearFieldErrors(form) {
    form.querySelectorAll('[data-error-for]').forEach((node) => {
      node.textContent = '';
    });
  }

  function showStatus(form, message, success = false) {
    const status = form.querySelector('[data-wbu-form-status]');
    if (!status) return;

    status.textContent = message;
    status.classList.add('is-visible');
    status.classList.toggle('is-success', success);
  }

  function clearStatus(form) {
    const status = form.querySelector('[data-wbu-form-status]');
    if (!status) return;

    status.textContent = '';
    status.classList.remove('is-visible', 'is-success');
  }

  function resetSuccessState(modal) {
    modal.classList.remove('is-success');

    const requestId = modal.querySelector('[data-wbu-request-id]');
    if (requestId) requestId.textContent = '';
  }

  function moveModalToBody(modal) {
    if (modal.parentNode === document.body) return;

    portalOwners.set(modal, {
      parent: modal.parentNode,
      nextSibling: modal.nextSibling
    });

    document.body.appendChild(modal);
  }

  function restoreModal(modal) {
    const owner = portalOwners.get(modal);
    if (!owner || !owner.parent) return;

    if (
      owner.nextSibling &&
      owner.nextSibling.parentNode === owner.parent
    ) {
      owner.parent.insertBefore(modal, owner.nextSibling);
    } else {
      owner.parent.appendChild(modal);
    }

    portalOwners.delete(modal);
  }

  function openModal(name, trigger) {
    const modal = document.querySelector(
      `${modalSelector}[data-wbu-form-modal="${name}"]`
    );

    if (!modal) return;

    lastTrigger = trigger || document.activeElement;
    activeModal = modal;

    moveModalToBody(modal);

    const form = modal.querySelector('[data-wbu-request-form]');

    resetSuccessState(modal);

    if (form) {
      clearFieldErrors(form);
      clearStatus(form);
      form.dataset.startedAt = String(Date.now());
    }

    modal.hidden = false;
    document.body.classList.add('wbu-policy-form-lock');

    requestAnimationFrame(() => {
      const firstInput = modal.querySelector(
        'input:not([tabindex="-1"]), textarea, button'
      );

      if (firstInput) firstInput.focus();
    });
  }

  function closeModal(modal = activeModal) {
    if (!modal) return;

    modal.hidden = true;
    document.body.classList.remove('wbu-policy-form-lock');

    const form = modal.querySelector('[data-wbu-request-form]');
    if (form) {
      form.reset();
      delete form.dataset.startedAt;
      clearFieldErrors(form);
      clearStatus(form);
    }

    resetSuccessState(modal);

    restoreModal(modal);

    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }

    activeModal = null;
    lastTrigger = null;
  }

  function getFocusable(modal) {
    return Array.from(
      modal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((node) => !node.hidden && node.offsetParent !== null);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-wbu-form-open]');
    if (opener) {
      event.preventDefault();
      openModal(opener.dataset.wbuFormOpen, opener);
      return;
    }

    const closer = event.target.closest('[data-wbu-form-close]');
    if (closer) {
      event.preventDefault();
      closeModal(closer.closest(modalSelector) || activeModal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal || activeModal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusable(activeModal);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-wbu-request-form]');
    if (!form) return;

    event.preventDefault();

    const modal = form.closest(modalSelector);
    const endpoint = form.dataset.endpoint;

    if (!modal || !endpoint) return;

    clearFieldErrors(form);
    clearStatus(form);

    const submitButton = form.querySelector('button[type="submit"]');
    const originalLabel = submitButton ? submitButton.textContent : '';

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }

    const payload = {
      first_name: form.elements.first_name.value.trim(),
      last_name: form.elements.last_name.value.trim(),
      email: form.elements.email.value.trim(),
      order_number: form.elements.order_number.value.trim(),
      message: form.elements.message.value.trim(),
      contact_website: form.elements.contact_website.value,
      locale: getLocale(),
      source_url: window.location.href,
      started_at: Number(form.dataset.startedAt || Date.now())
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const raw = await response.text();
      let data = null;

      try {
        data = JSON.parse(raw);
      } catch (_) {
        throw new Error(
          `HTTP ${response.status}. The server returned an invalid response.`
        );
      }

      if (data.fields) {
        Object.entries(data.fields).forEach(([field, message]) => {
          const target = form.querySelector(
            `[data-error-for="${field}"]`
          );

          if (target) target.textContent = message;
        });
      }

      if (!response.ok && !data.ok) {
        if (data.error === 'validation_error' && data.fields) {
          const firstFieldName = Object.keys(data.fields)[0];
          const firstField = firstFieldName
            ? form.elements[firstFieldName]
            : null;

          if (firstField && typeof firstField.focus === 'function') {
            firstField.focus();
          }

          return;
        }

        throw new Error(
          data.message ||
          data.error ||
          'The request could not be sent.'
        );
      }

      if (data.ok && data.request_id) {
        const requestId = modal.querySelector('[data-wbu-request-id]');
        if (requestId) requestId.textContent = data.request_id;

        modal.classList.add('is-success');

        const closeButton = modal.querySelector(
          '.wbu-policy-form__success-close'
        );

        if (closeButton) closeButton.focus();

        return;
      }

      showStatus(
        form,
        data.message || 'Your request has been recorded.',
        Boolean(data.ok)
      );
    } catch (error) {
      showStatus(
        form,
        error instanceof Error
          ? error.message
          : 'The request could not be sent.'
      );
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalLabel;
      }
    }
  });
})();
